import {
  IMetar,
  ITaf,
  ICommonForecastProperties,
  IMetarForecast,
  IAftnMessage,
  TAftnContentType,
} from "@naviair-utm/node-shared-interfaces";
import {
  getIntFromRegExp,
  getStringFromRegExp,
  trimmer,
} from "../CommonParsers";
import * as parsers from "./MetarTafParsers";
import {
  headerLineParser,
  icaoRegex,
  metarBulletinRegex,
  metarChangeRegex,
  metarWeatherSplitRegex,
  qnhRegex,
  speciBulletinRegex,
  tafBulletinRegex,
} from "./MetarTafRegExp";
import { newlineRegex } from "../CommonRegExp";
import { contentHeaderMapping } from "./MetarContentHeaderMap";

/**
 * metarParser
 * @param string input Metar weather string
 * @returns @see IMetarParsed
 */
export const metarParser = (input: string): IAftnMessage<IMetar>[] => {
  // Replace any newline with a space - we don't need that in the parser
  const metarCleaned = input.replace(newlineRegex, " ").replace(/\x0A/g, " ");

  // The list of types that a METAR should match
  const metarTypes: TAftnContentType[] = [
    'METAR',
    'SPECI'
  ];
  // Determine type from the content by matching the array of types
  let type: TAftnContentType = metarTypes.find((type) => metarCleaned.includes(type));
  if (!type) type = 'UNKNOWN';

  // If the type cannot be found from the content, parse the content header and search for the 2 letters that tell the type
  if (type === 'UNKNOWN') {
    const matchedHeader = input.match(headerLineParser);
    if (matchedHeader) type = contentHeaderMapping[matchedHeader[1]] ?? 'UNKNOWN';
  }

  // Split input so each metar is parsed separately if bulletin - if not, then the array just contains 1 element
  let metarArrMatch;
  if (type === 'SPECI' || type === 'SPECI COR') {
    metarArrMatch = metarCleaned.match(speciBulletinRegex);
  } else {
    metarArrMatch = metarCleaned.match(metarBulletinRegex);
  }

  // Loop through array and parse each metar individually
  if (metarArrMatch && metarArrMatch !== null) {
    try {
      return metarArrMatch.map((val) => {
        const metarParsed: IAftnMessage<IMetar> = {} as IAftnMessage<IMetar>;
        metarParsed.parsedMessage = {} as IMetar;
        metarParsed.type = type;
        const trimmedMetar = trimmer(val);
        metarParsed.rawInput = trimmedMetar;

        // Get the ICAO code from the METAR
        const icao = getStringFromRegExp(trimmedMetar, icaoRegex);
        if (icao !== null) {
          metarParsed.icao = icao;
        }

        // Get the date which consists of day, hour and minute
        const date = parsers.dateParser(trimmedMetar);
        if (date) {
          metarParsed.validity = {};
          metarParsed.validity.start = date;
        }

        // Check if the METAR contains "AUTO" which means that it is auto generated
        metarParsed.parsedMessage.auto = parsers.autoParser(trimmedMetar);

        // Split the current and forecasted weather. Index 0 will be current and index 1 will be forecasted
        const metarWeatherGroups = trimmedMetar.split(metarWeatherSplitRegex);
        for (let i = 0; i < metarWeatherGroups.length; i++) {
          const weather: ICommonForecastProperties =
            parsers.commonForecastParser(metarWeatherGroups[i]);
          if (weather && weather !== null) {
            // Assign the results to an empty object of IMetarForecast type
            let currentWeather: IMetarForecast = {};
            Object.assign(currentWeather, weather);

            // Search for one of three period types
            const periodType: string = getStringFromRegExp(
              metarWeatherGroups[i],
              metarChangeRegex
            );
            if (periodType !== null) {
              currentWeather.periodType = periodType;
            }

            // First group/loop is current weather, second is forecast
            if (i === 0) {
              metarParsed.parsedMessage.currentWeather = currentWeather;
            } else if (i === 1) {
              metarParsed.parsedMessage.trendForecast = currentWeather;
            }
          }
        }

        // Get temperature which consists of the temperature and a dew point
        const temp = parsers.tempParser(trimmedMetar);
        if (temp !== null) {
          metarParsed.parsedMessage.temp = temp.temp;
          metarParsed.parsedMessage.dewPoint = temp.dewPoint;
        }

        // Get the QNH if present
        const qnh = getIntFromRegExp(trimmedMetar, qnhRegex);
        if (qnh !== null) {
          metarParsed.parsedMessage.qnh = qnh;
        }

        // Check if the METAR has been corrected and set true if so
        metarParsed.parsedMessage.corrected = parsers.corParser(trimmedMetar);

        // Check if the visual range of the runways has been provided
        const rwys = parsers.rwyVisualRng(trimmedMetar);
        if (rwys !== null) {
          metarParsed.parsedMessage.rwysVisualRange = rwys;
        }

        return metarParsed;
      });
    } catch (e) {
      const metarParsed: IAftnMessage<IMetar> = {} as IAftnMessage<IMetar>;
      metarParsed.errors = [];
      metarParsed.errors.push("Failed mapping array");
      metarParsed.errors.push(e);
      metarParsed.type = "METAR";
      metarParsed.rawInput = trimmer(metarCleaned);
      return [metarParsed];
    }
  } else {
    const metarParsed: IAftnMessage<IMetar> = {} as IAftnMessage<IMetar>;
    metarParsed.errors.push("Failed metarArrMatch");
    metarParsed.type = "METAR";
    metarParsed.rawInput = trimmer(metarCleaned);
    return [metarParsed];
  }
};

/**
 * tafParser
 * @param {string} input Taf weather forecast
 * @returns {ITafParsed}
 */

export const tafParser = (input: string): IAftnMessage<ITaf>[] => {
  // Replace any newline with a space - we don't need that in the parser
  const tafCleaned = input.replace(newlineRegex, " ").replace(/\x0A/g, " ");
  // Split input so each metar is parsed separately if bulletin - if not, then the array just contains 1 element
  const tafArrMatch = tafCleaned.match(tafBulletinRegex);

  // Parse each element in array and return
  if (tafArrMatch && tafArrMatch !== null) {
    try {
      return tafArrMatch.map((val) => {
        const tafParsed: IAftnMessage<ITaf> = {} as IAftnMessage<ITaf>;
        tafParsed.parsedMessage = {} as ITaf;

        // Trim the message for weird whitespces and such
        const trimmedTaf = trimmer(val);
        // Set raw input
        tafParsed.rawInput = trimmedTaf;

        // The types that a TAF should match
        const tafTypes: TAftnContentType[] = [
          'TAF'
        ];
        // Determine type from the content by matching the array of types
        let type: TAftnContentType = tafTypes.find((type) => trimmedTaf.includes(type));
        if (!type) type = 'UNKNOWN';

        // If the type cannot be found from the content, parse the content header and search for the 2 letters that tell the type
        if (type === 'UNKNOWN') {
          const matchedHeader = input.match(headerLineParser);
          if (matchedHeader) type = contentHeaderMapping[matchedHeader[1]] ?? 'UNKNOWN';
        }
        // Set the type
        tafParsed.type = type;


        // Get ICAO code from TAF
        tafParsed.icao = getStringFromRegExp(trimmedTaf, icaoRegex);

        // Get date issued with same format as in METARs
        const dateIssued = parsers.dateParser(trimmedTaf);
        if (dateIssued) {
          tafParsed.parsedMessage.forecastIssued = dateIssued;
        } else {
          tafParsed.errors.push(
            "Failed parsing the date and time the taf was issued"
          );
        }

        // Get validity period and the hours between start and end
        const validity = parsers.tafValidityParser(trimmedTaf);
        if (validity) {
          tafParsed.validity = {
            start: validity.start,
            end: validity.end,
          };
          tafParsed.parsedMessage.validityPeriod = validity.validityPeriod;
        } else {
          tafParsed.errors.push(
            "Failed parsing validity of taf. This will affect validity period of the forecast groups as welK"
          );
        }

        // Split the taf report into groups consisting of forecast periods with weather conditions,
        // then parse these individually, which is done in the groups parser
        const tafGroups = parsers.tafGroupsParser(trimmedTaf, validity);
        if (tafGroups !== null) {
          tafParsed.parsedMessage.forecasts = tafGroups;
        } else {
          tafParsed.errors.push("Failed parsing taf forecast groups");
        }

        return tafParsed;
      });
    } catch (e) {
      const tafParsed: IAftnMessage<ITaf> = {} as IAftnMessage<ITaf>;
      tafParsed.errors = [];
      tafParsed.errors.push("Failed mapping array");
      tafParsed.errors.push(e);
      tafParsed.type = "TAF";
      tafParsed.rawInput = trimmer(tafCleaned);
      return [tafParsed];
    }
  } else {
    const metarParsed: IAftnMessage<ITaf> = {} as IAftnMessage<ITaf>;
    metarParsed.errors.push("Failed tafArrMatch");
    metarParsed.type = "TAF";
    metarParsed.rawInput = trimmer(tafCleaned);
    return [metarParsed];
  }
};
